interface ContentBlock {
  type?: unknown;
  props?: Record<string, unknown>;
}

export interface SeoRuleResult {
  id: string;
  label: string;
  passed: boolean;
  score: number;
  detail: string;
}

export interface SeoScore {
  score: number;
  rules: SeoRuleResult[];
}

interface ExtractedContent {
  text: string;
  paragraphs: string[];
  wordCount: number;
  hasInternalLink: boolean;
  imageCount: number;
  imagesWithAlt: number;
  headingCount: number;
}

const SAME_SITE_HOSTS = ["stero.ai", "pages.stero.ai", "app.stero.ai"];

export function scoreSeo(
  content: unknown,
  title: string,
  slug: string,
  focusKeyword: string,
  metaDescription = "",
): SeoScore {
  const normalizedKeyword = normalizeText(focusKeyword);
  const extracted = extractContent(content);
  const normalizedTitle = normalizeText(title);
  const normalizedSlug = normalizeText(slug);
  const normalizedDescription = metaDescription.trim();
  const firstParagraph = normalizeText(extracted.paragraphs[0] ?? "");
  const density = normalizedKeyword
    ? calculateKeywordDensity(extracted.text, normalizedKeyword)
    : 0;

  const rules: SeoRuleResult[] = [
    buildRule(
      "keyword-title",
      "Keyword in title",
      normalizedKeyword.length > 0 && normalizedTitle.includes(normalizedKeyword),
      normalizedKeyword
        ? "Focus keyword appears in the SEO title."
        : "Add a focus keyword to evaluate title alignment.",
      normalizedKeyword
        ? "Include the focus keyword in the title."
        : "Set a focus keyword first.",
    ),
    buildRule(
      "keyword-first-paragraph",
      "Keyword in first paragraph",
      normalizedKeyword.length > 0 && firstParagraph.includes(normalizedKeyword),
      normalizedKeyword
        ? "Focus keyword appears in the opening paragraph."
        : "Add a focus keyword to evaluate the intro.",
      normalizedKeyword
        ? "Work the focus keyword into the first paragraph."
        : "Set a focus keyword first.",
    ),
    buildRule(
      "keyword-density",
      "Keyword density",
      normalizedKeyword.length > 0 && density >= 0.5 && density <= 2.5,
      normalizedKeyword
        ? `Keyword density is ${density.toFixed(1)}%, within the target range.`
        : "Add a focus keyword to score keyword density.",
      normalizedKeyword
        ? `Keyword density is ${density.toFixed(1)}%; target 0.5% to 2.5%.`
        : "Set a focus keyword first.",
    ),
    buildRule(
      "title-length",
      "Title length",
      title.trim().length >= 30 && title.trim().length <= 60,
      `Title length is ${title.trim().length} characters.`,
      `Title length is ${title.trim().length}; target 30 to 60 characters.`,
    ),
    buildRule(
      "meta-description",
      "Meta description length",
      normalizedDescription.length >= 120 && normalizedDescription.length <= 160,
      `Meta description length is ${normalizedDescription.length} characters.`,
      `Meta description length is ${normalizedDescription.length}; target 120 to 160 characters.`,
    ),
    buildRule(
      "internal-links",
      "Internal links",
      extracted.hasInternalLink,
      "The article body includes at least one internal link.",
      "Add a link to another Stero page or related article.",
    ),
    buildRule(
      "image-alt",
      "Image alt text",
      extracted.imageCount === 0 || extracted.imageCount === extracted.imagesWithAlt,
      extracted.imageCount === 0
        ? "No images found, so there are no missing alt attributes."
        : `${extracted.imagesWithAlt} of ${extracted.imageCount} images include alt text.`,
      extracted.imageCount === 0
        ? "Consider adding a relevant image."
        : `${extracted.imageCount - extracted.imagesWithAlt} images still need alt text.`,
    ),
    buildRule(
      "slug-keyword",
      "Keyword in slug",
      normalizedKeyword.length > 0 && normalizedSlug.includes(normalizedKeyword),
      normalizedKeyword
        ? "The slug reflects the focus keyword."
        : "Add a focus keyword to evaluate the slug.",
      normalizedKeyword
        ? "Include the focus keyword in the slug."
        : "Set a focus keyword first.",
    ),
    buildRule(
      "word-count",
      "Word count",
      extracted.wordCount >= 300,
      `Article body contains ${extracted.wordCount} words.`,
      `Article body contains ${extracted.wordCount} words; target at least 300.`,
    ),
    buildRule(
      "headings",
      "Heading coverage",
      extracted.headingCount >= 2,
      `Article body contains ${extracted.headingCount} headings.`,
      `Article body contains ${extracted.headingCount} headings; target at least 2.`,
    ),
  ];

  return {
    score: Math.round(rules.reduce((total, rule) => total + rule.score, 0)),
    rules,
  };
}

function buildRule(
  id: string,
  label: string,
  passed: boolean,
  successDetail: string,
  failureDetail: string,
): SeoRuleResult {
  return {
    id,
    label,
    passed,
    score: passed ? 10 : 0,
    detail: passed ? successDetail : failureDetail,
  };
}

function extractContent(content: unknown): ExtractedContent {
  const blocks = readBlocks(content);
  const paragraphs: string[] = [];
  const allText: string[] = [];
  let hasInternalLink = false;
  let imageCount = 0;
  let imagesWithAlt = 0;
  let headingCount = 0;

  for (const block of blocks) {
    const type = typeof block.type === "string" ? block.type : "";
    const props = block.props ?? {};
    const textFragments = collectText(props);
    const joinedText = textFragments.join(" ").trim();

    if (joinedText) {
      allText.push(joinedText);
    }

    if (type === "Heading" || type === "Hero" || hasHeadingProps(props)) {
      headingCount += 1;
    }

    if (
      type === "RichText" ||
      type === "Text" ||
      type === "Blockquote" ||
      type === "CodeBlock"
    ) {
      if (joinedText) {
        paragraphs.push(joinedText);
      }
    }

    if (hasImageProps(type, props)) {
      imageCount += 1;
      if (hasNonEmptyString(props.alt) || hasNonEmptyString(props.imageAlt)) {
        imagesWithAlt += 1;
      }
    }

    if (containsInternalLink(props)) {
      hasInternalLink = true;
    }
  }

  const text = normalizeText(allText.join(" "));
  const wordCount = text ? text.split(/\s+/).filter(Boolean).length : 0;

  return {
    text,
    paragraphs: paragraphs.map((paragraph) => normalizeText(paragraph)).filter(Boolean),
    wordCount,
    hasInternalLink,
    imageCount,
    imagesWithAlt,
    headingCount,
  };
}

function readBlocks(content: unknown): ContentBlock[] {
  if (Array.isArray(content)) {
    return content.filter(isBlock);
  }

  if (content && typeof content === "object") {
    const maybeContent = (content as { content?: unknown }).content;
    if (Array.isArray(maybeContent)) {
      return maybeContent.filter(isBlock);
    }
  }

  return [];
}

function isBlock(value: unknown): value is ContentBlock {
  return Boolean(value) && typeof value === "object";
}

function collectText(input: unknown): string[] {
  if (typeof input === "string") {
    return [stripHtml(input)];
  }

  if (Array.isArray(input)) {
    return input.flatMap((item) => collectText(item));
  }

  if (!input || typeof input !== "object") {
    return [];
  }

  const values = Object.values(input as Record<string, unknown>);
  return values.flatMap((value) => collectText(value));
}

function containsInternalLink(props: Record<string, unknown>): boolean {
  return Object.values(props).some((value) => {
    if (typeof value !== "string") {
      return false;
    }

    const candidate = value.trim();
    if (!candidate) {
      return false;
    }

    if (candidate.startsWith("/")) {
      return true;
    }

    if (!candidate.startsWith("http://") && !candidate.startsWith("https://")) {
      return false;
    }

    try {
      const url = new URL(candidate);
      return SAME_SITE_HOSTS.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`));
    } catch {
      return false;
    }
  });
}

function hasImageProps(type: string, props: Record<string, unknown>): boolean {
  return (
    type === "Image" ||
    type === "ImageEnhanced" ||
    hasNonEmptyString(props.imageUrl) ||
    hasNonEmptyString(props.src) ||
    hasNonEmptyString(props.image)
  );
}

function hasHeadingProps(props: Record<string, unknown>): boolean {
  return hasNonEmptyString(props.heading) || hasNonEmptyString(props.title);
}

function hasNonEmptyString(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

function stripHtml(value: string): string {
  return value
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function calculateKeywordDensity(text: string, keyword: string): number {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0 || keyword.length === 0) {
    return 0;
  }

  const matches = text.split(keyword).length - 1;
  return (matches / words.length) * 100;
}
